import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Play, RotateCcw, Check, X, AlertCircle, Map as MapIcon } from 'lucide-react';

// --- Scenarios / Graph Data ---

const SCENARIOS = [
  {
    name: "Standard Example",
    nodes: [
      { id: 'A', x: 100, y: 60 },
      { id: 'B', x: 250, y: 60 },
      { id: 'C', x: 100, y: 200 },
      { id: 'D', x: 250, y: 200 },
      { id: 'E', x: 400, y: 130 },
      { id: 'F', x: 250, y: 320 },
    ],
    edges: [
      { u: 'A', v: 'B', w: 2, id: 'e1' },
      { u: 'A', v: 'C', w: 4, id: 'e2' },
      { u: 'B', v: 'C', w: 1, id: 'e3' },
      { u: 'B', v: 'D', w: 3, id: 'e4' },
      { u: 'B', v: 'E', w: 10, id: 'e5' },
      { u: 'C', v: 'D', w: 5, id: 'e6' },
      { u: 'D', v: 'E', w: 7, id: 'e7' },
      { u: 'D', v: 'F', w: 8, id: 'e8' },
      { u: 'C', v: 'F', w: 6, id: 'e9' },
    ]
  },
  {
    name: "Square with Diagonal",
    nodes: [
      { id: 'A', x: 150, y: 100 },
      { id: 'B', x: 350, y: 100 },
      { id: 'C', x: 150, y: 300 },
      { id: 'D', x: 350, y: 300 },
    ],
    edges: [
      { u: 'A', v: 'B', w: 2, id: 's2-1' },
      { u: 'C', v: 'D', w: 1, id: 's2-2' },
      { u: 'A', v: 'C', w: 5, id: 's2-3' },
      { u: 'B', v: 'D', w: 6, id: 's2-4' },
      { u: 'B', v: 'C', w: 3, id: 's2-5' }, // Diagonal
    ]
  },
  {
    name: "Two Clusters (Bridge)",
    nodes: [
      { id: 'A', x: 60, y: 100 },
      { id: 'B', x: 140, y: 50 },
      { id: 'C', x: 140, y: 150 },
      { id: 'D', x: 360, y: 150 },
      { id: 'E', x: 360, y: 50 },
      { id: 'F', x: 440, y: 100 },
    ],
    edges: [
      { u: 'A', v: 'B', w: 2, id: 's3-1' },
      { u: 'A', v: 'C', w: 3, id: 's3-2' },
      { u: 'B', v: 'C', w: 1, id: 's3-3' },
      { u: 'C', v: 'D', w: 8, id: 's3-4' }, // The Bridge
      { u: 'D', v: 'E', w: 2, id: 's3-5' },
      { u: 'E', v: 'F', w: 3, id: 's3-6' },
      { u: 'D', v: 'F', w: 1, id: 's3-7' },
    ]
  },
  {
    name: "Hub & Spoke",
    nodes: [
      { id: 'A', x: 250, y: 180 }, // Center
      { id: 'B', x: 250, y: 50 },
      { id: 'C', x: 400, y: 150 },
      { id: 'D', x: 350, y: 320 },
      { id: 'E', x: 150, y: 320 },
      { id: 'F', x: 100, y: 150 },
    ],
    edges: [
      { u: 'A', v: 'B', w: 2, id: 's4-1' },
      { u: 'A', v: 'C', w: 3, id: 's4-2' },
      { u: 'A', v: 'D', w: 4, id: 's4-3' },
      { u: 'A', v: 'E', w: 3, id: 's4-4' },
      { u: 'A', v: 'F', w: 2, id: 's4-5' },
      // Rim
      { u: 'B', v: 'C', w: 8, id: 's4-6' },
      { u: 'C', v: 'D', w: 9, id: 's4-7' },
      { u: 'D', v: 'E', w: 8, id: 's4-8' },
      { u: 'E', v: 'F', w: 9, id: 's4-9' },
      { u: 'F', v: 'B', w: 8, id: 's4-10' },
    ]
  }
];

const NODE_RADIUS = 18;

export default function KruskalsAlgo() {
  // --- State ---
  const [scenarioIndex, setScenarioIndex] = useState(0);
  const [currentStep, setCurrentStep] = useState(-1); // -1 = Not started
  const [mstEdges, setMstEdges] = useState([]); // Array of edge IDs
  const [rejectedEdges, setRejectedEdges] = useState([]); // Array of edge IDs
  const [parents, setParents] = useState({}); // Map { nodeId: parentId }
  const [treeSizes, setTreeSizes] = useState({}); // Map { nodeId: size } for Union by Size
  const [feedback, setFeedback] = useState({ type: 'neutral', msg: "Press Start to begin Kruskal's Algorithm." });
  const [finished, setFinished] = useState(false);

  const currentScenario = SCENARIOS[scenarioIndex];

  // Sort edges whenever scenario changes
  const sortedEdges = useMemo(() => {
    return [...currentScenario.edges].sort((a, b) => a.w - b.w);
  }, [currentScenario]);

  const reset = useCallback(() => {
    const initialParents = {};
    const initialSizes = {};

    currentScenario.nodes.forEach(n => {
      initialParents[n.id] = n.id;
      initialSizes[n.id] = 1; // each set starts size 1
    });

    setParents(initialParents);
    setTreeSizes(initialSizes);
    setCurrentStep(-1);
    setMstEdges([]);
    setRejectedEdges([]);
    setFinished(false);
    setFeedback({
      type: 'neutral',
      msg: "Press Start to begin Kruskal's Algorithm."
    });
  }, [currentScenario]);

  // initialize whenever scenario changes
  useEffect(() => {
    reset();
  }, [reset]);

  // --- Union-Find Logic ---

  // --- Union-Find Logic ---

  // Find root with NO path compression (recursive look up)
  const find = (nodeId, currentParents) => {
    if (currentParents[nodeId] === nodeId) return nodeId;
    return find(currentParents[nodeId], currentParents);
  };

  // Union by Size (Merge smaller tree into larger tree)
  const union = (nodeU, nodeV) => {
    const rootU = find(nodeU, parents);
    const rootV = find(nodeV, parents);

    if (rootU !== rootV) {
      const sizeU = treeSizes[rootU];
      const sizeV = treeSizes[rootV];

      let newParents = { ...parents };
      let newSizes = { ...treeSizes };

      // Always attach smaller tree to larger tree
      if (sizeU < sizeV) {
        newParents[rootU] = rootV; // V becomes parent of U
        newSizes[rootV] += sizeU;  // Update size of V
      } else {
        newParents[rootV] = rootU; // U becomes parent of V
        newSizes[rootU] += sizeV;  // Update size of U
      }

      setParents(newParents);
      setTreeSizes(newSizes);
    }
  };

  // --- Interactive Logic ---

  const start = () => {
    setCurrentStep(0);
    setFeedback({ type: 'info', msg: 'Consider the highlighted edge. Does it form a cycle?' });
  };

  const handleDecision = (action) => {
    if (currentStep >= sortedEdges.length) return;

    const edge = sortedEdges[currentStep];
    const rootU = find(edge.u, parents);
    const rootV = find(edge.v, parents);
    const formsCycle = rootU === rootV;

    if (action === 'ADD') {
      if (formsCycle) {
        setFeedback({ type: 'error', msg: `Incorrect! Adding (${edge.u}, ${edge.v}) creates a cycle because they share the root ${rootU}.` });
        return; // Don't advance
      }
      // Correct add
      union(edge.u, edge.v);
      setMstEdges([...mstEdges, edge.id]);
      advance();
    } else if (action === 'SKIP') {
      if (!formsCycle) {
        setFeedback({ type: 'error', msg: `Incorrect! (${edge.u}, ${edge.v}) connects two different components (${rootU} and ${rootV}). It is safe to add.` });
        return; // Don't advance
      }
      // Correct skip
      setRejectedEdges([...rejectedEdges, edge.id]);
      advance();
    }
  };

  const advance = () => {
    const nextStep = currentStep + 1;
    if (nextStep >= sortedEdges.length) {
      setCurrentStep(nextStep);
      setFinished(true);
      setFeedback({ type: 'success', msg: 'Algorithm Complete! MST found.' });
    } else {
      setCurrentStep(nextStep);
      setFeedback({ type: 'info', msg: 'Consider the next edge.' });
    }
  };

  // --- Tree Layout Algorithm for Right Panel ---
  const treeLayout = useMemo(() => {
    const roots = currentScenario.nodes.filter(n => parents[n.id] === n.id);
    const layout = {};

    const childrenMap = {};
    currentScenario.nodes.forEach(n => {
      const p = parents[n.id];
      if (p !== n.id) {
        if (!childrenMap[p]) childrenMap[p] = [];
        childrenMap[p].push(n.id);
      }
    });

    let currentX = 0;
    const assignCoords = (nodeId, level) => {
      const children = childrenMap[nodeId] || [];
      const childrenCoords = children.map(c => assignCoords(c, level + 1));

      const myWidth = 60;
      let myX;

      if (children.length === 0) {
        myX = currentX + myWidth / 2;
        currentX += myWidth;
      } else {
        const minChildX = childrenCoords[0].x;
        const maxChildX = childrenCoords[childrenCoords.length - 1].x;
        myX = (minChildX + maxChildX) / 2;
      }

      layout[nodeId] = { x: myX, y: 50 + (level * 80) };
      return layout[nodeId];
    };

    roots.sort((a, b) => a.id.localeCompare(b.id)).forEach(root => {
      assignCoords(root.id, 0);
      currentX += 40;
    });

    return layout;
  }, [parents, currentScenario]);


  // --- Render Helpers ---

  const getEdgeColor = (edge) => {
    if (mstEdges.includes(edge.id)) return '#22c55e';
    if (rejectedEdges.includes(edge.id)) return '#ef4444';
    if (currentStep >= 0 && currentStep < sortedEdges.length && sortedEdges[currentStep].id === edge.id) return '#eab308';
    return '#d1d5db';
  };

  const getEdgeWidth = (edge) => {
    if (mstEdges.includes(edge.id)) return 4;
    if (currentStep >= 0 && currentStep < sortedEdges.length && sortedEdges[currentStep].id === edge.id) return 4;
    return 2;
  };

  const currentEdge = currentStep >= 0 && currentStep < sortedEdges.length ? sortedEdges[currentStep] : null;

  return (
    <div className="min-h-[700px] bg-slate-50 flex flex-col font-sans text-slate-800 overflow-hidden">

      {/* Header / Controls */}
      <div className="bg-white shadow-sm p-2 border-b border-slate-200 flex-shrink-0 z-10">
        <div className="max-w-6xl mx-auto w-full">
          <div className="flex flex-col md:flex-row justify-between items-center gap-2 mb-2">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-slate-800">Kruskal's & Union-Find</h1>

              {/* Scenario Selector */}
              <div className="flex items-center ml-4 bg-slate-100 rounded-lg p-1 border border-slate-200">
                <MapIcon size={14} className="text-slate-400 ml-2 mr-1" />
                <select
                  className="bg-transparent text-sm font-medium text-slate-700 outline-none border-none pr-2 cursor-pointer"
                  value={scenarioIndex}
                  onChange={(e) => setScenarioIndex(Number(e.target.value))}
                >
                  {SCENARIOS.map((s, idx) => (
                    <option key={idx} value={idx}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-2">
               {currentStep === -1 && (
                 <button onClick={start} className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition shadow-sm">
                   <Play size={16} /> Start
                 </button>
               )}
               <button onClick={reset} className="flex items-center gap-1 px-3 py-1.5 border border-slate-300 text-slate-600 text-sm rounded-md hover:bg-slate-100 transition shadow-sm">
                 <RotateCcw size={16} /> Reset
               </button>
            </div>
          </div>

          {/* Edge Queue Bar */}
          <div className="flex items-center gap-1 overflow-x-auto py-1 pb-2 scrollbar-hide">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide mr-2 whitespace-nowrap">Sorted Edges:</span>
            {sortedEdges.map((edge, idx) => {
              const isCurrent = idx === currentStep;
              const isMST = mstEdges.includes(edge.id);
              const isRejected = rejectedEdges.includes(edge.id);

              let statusClass = "bg-white border-slate-300 text-slate-600";
              if (isCurrent) statusClass = "bg-yellow-100 border-yellow-500 text-yellow-900 shadow-md scale-110 font-bold ring-2 ring-yellow-200";
              else if (isMST) statusClass = "bg-green-100 border-green-500 text-green-800 opacity-70";
              else if (isRejected) statusClass = "bg-red-50 border-red-300 text-red-400 opacity-50 line-through decoration-2";

              return (
                <div
                  key={edge.id}
                  className={`flex-shrink-0 flex flex-col items-center justify-center w-10 h-10 border rounded transition-all duration-300 ${statusClass}`}
                >
                  <div className="text-[10px] leading-none">{edge.u}-{edge.v}</div>
                  <div className="text-xs font-bold leading-none">{edge.w}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-2 min-h-0">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-4 h-full">

          {/* Left Panel: The Graph */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-2 flex flex-col h-full">
            <h2 className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-2 flex-shrink-0">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div> Graph Topology
            </h2>
            <div className="flex-1 bg-slate-50 rounded border border-slate-100 relative overflow-hidden flex items-center justify-center">
              <svg className="w-full h-full max-h-[60vh]" viewBox="0 0 500 400" preserveAspectRatio="xMidYMid meet">
                {/* Edges */}
                {currentScenario.edges.map((edge) => {
                    const u = currentScenario.nodes.find(n => n.id === edge.u);
                    const v = currentScenario.nodes.find(n => n.id === edge.v);
                    return (
                      <g key={edge.id}>
                        <line
                          x1={u.x} y1={u.y}
                          x2={v.x} y2={v.y}
                          stroke={getEdgeColor(edge)}
                          strokeWidth={getEdgeWidth(edge)}
                          strokeDasharray={rejectedEdges.includes(edge.id) ? "5,5" : "none"}
                          className="transition-all duration-300"
                        />
                        {/* Weight Label */}
                        <rect
                          x={(u.x + v.x)/2 - 8}
                          y={(u.y + v.y)/2 - 8}
                          width="16" height="16"
                          fill="white"
                          opacity="0.8"
                          rx="3"
                        />
                        <text
                          x={(u.x + v.x) / 2}
                          y={(u.y + v.y) / 2}
                          textAnchor="middle"
                          dy="4"
                          className="text-[10px] font-bold fill-slate-600 select-none"
                        >
                          {edge.w}
                        </text>
                      </g>
                    );
                })}

                {/* Nodes */}
                {currentScenario.nodes.map((node) => (
                  <g key={node.id} className="transition-transform duration-300">
                    <circle
                      cx={node.x}
                      cy={node.y}
                      r={NODE_RADIUS}
                      className="fill-white stroke-slate-400 stroke-2"
                    />
                    <text
                      x={node.x}
                      y={node.y}
                      dy="4"
                      textAnchor="middle"
                      className="font-bold fill-slate-700 text-xs select-none"
                    >
                      {node.id}
                    </text>
                  </g>
                ))}
              </svg>
            </div>
          </div>

          {/* Right Panel: Union Find Forest */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-2 flex flex-col h-full">
            <h2 className="text-sm font-semibold text-slate-700 mb-1 flex items-center gap-2 flex-shrink-0">
              <div className="w-2.5 h-2.5 rounded-full bg-purple-500"></div> Union-Find Forest
            </h2>
            <div className="text-[10px] text-slate-400 mb-1 flex-shrink-0">
               Arrows point to parent. (Union by Size implemented)
            </div>
            <div className="flex-1 bg-slate-50 rounded border border-slate-100 relative overflow-hidden flex items-center justify-center">
              <svg className="w-full h-full max-h-[60vh]" viewBox="0 0 500 400" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="22" refY="3.5" orient="auto">
                      <polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8" />
                    </marker>
                </defs>

                {/* Forest Edges (Child -> Parent) */}
                {currentScenario.nodes.map(node => {
                   const parentId = parents[node.id];
                   if (parentId === node.id) return null;

                   const start = treeLayout[node.id];
                   const end = treeLayout[parentId];
                   if (!start || !end) return null;

                   return (
                     <line
                        key={`link-${node.id}`}
                        x1={start.x} y1={start.y}
                        x2={end.x} y2={end.y}
                        stroke="#94a3b8"
                        strokeWidth="2"
                        markerEnd="url(#arrowhead)"
                        className="transition-all duration-500 ease-in-out"
                     />
                   )
                })}

                {/* Forest Nodes */}
                {currentScenario.nodes.map(node => {
                    const pos = treeLayout[node.id] || { x: 0, y: 0 };
                    const isCurrentContext = currentEdge && (node.id === currentEdge.u || node.id === currentEdge.v);

                    return (
                      <g key={`uf-${node.id}`} style={{ transform: `translate(${pos.x}px, ${pos.y}px)`}} className="transition-all duration-500 ease-in-out">
                         <circle
                          r={NODE_RADIUS}
                          className={`stroke-2 transition-colors duration-300 ${isCurrentContext ? 'fill-yellow-100 stroke-yellow-500' : 'fill-white stroke-purple-300'}`}
                        />
                        <text
                          dy="4"
                          textAnchor="middle"
                          className="font-bold fill-slate-700 text-xs select-none"
                        >
                          {node.id}
                        </text>
                      </g>
                    )
                })}
              </svg>
            </div>
          </div>

        </div>
      </div>

      {/* Controls & Feedback Bar */}
      <div className="bg-white border-t border-slate-200 p-3 flex-shrink-0">
        <div className="max-w-3xl mx-auto flex flex-col items-center gap-2">

          {/* Feedback Text */}
          <div className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm w-full justify-center
            ${feedback.type === 'error' ? 'bg-red-50 text-red-800 border border-red-100' : ''}
            ${feedback.type === 'success' ? 'bg-green-50 text-green-800 border border-green-100' : ''}
            ${feedback.type === 'info' ? 'bg-blue-50 text-blue-800 border border-blue-100' : ''}
            ${feedback.type === 'neutral' ? 'bg-slate-50 text-slate-600' : ''}
          `}>
            {feedback.type === 'error' && <AlertCircle className="w-4 h-4 flex-shrink-0" />}
            {feedback.type === 'success' && <Check className="w-4 h-4 flex-shrink-0" />}
            {feedback.type === 'info' && <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center font-bold text-xs bg-blue-200 rounded-full">?</div>}
            <p className="text-center">{feedback.msg}</p>
          </div>

          {/* Action Buttons */}
          {currentStep !== -1 && !finished && (
            <div className="flex gap-3">
              <button
                onClick={() => handleDecision('ADD')}
                className="flex items-center gap-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold shadow-md text-sm transition-transform active:scale-95"
              >
                <Check size={16} />
                Add
              </button>
              <button
                onClick={() => handleDecision('SKIP')}
                className="flex items-center gap-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold shadow-md text-sm transition-transform active:scale-95"
              >
                <X size={16} />
                Reject
              </button>
            </div>
          )}

          {finished && (
             <button
               onClick={reset}
               className="px-4 py-2 bg-slate-800 text-white text-sm rounded-lg font-semibold shadow-md hover:bg-slate-900 transition"
             >
               Restart Simulation
             </button>
          )}

        </div>
      </div>
    </div>
  );
}